import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthenticationType } from '../types/types.authentication';
import {
  Authentication,
  Token,
  ProjectRole,
  OrganizationRole,
  AuthenticateResponse,
} from '@rapidaai/react';
import { User } from '@rapidaai/react';
import { AuthorizeUser } from '@rapidaai/react';
import { ServiceError } from '@rapidaai/react';
import { connectionConfig, CONFIG } from '@/configs';

const REST_BASE = CONFIG.connection.web;

export const useAuthenticationStore = create<AuthenticationType>()(
  persist(
    (set, get) => ({
      currentUser: {} as User.AsObject,
      setCurrentUser: (u: User.AsObject) => set({ currentUser: u }),
      token: {} as Token.AsObject,
      setToken: (t: Token.AsObject) => set({ token: t }),
      setAuthentication: (
        a: Authentication | undefined,
        callback: () => void,
      ) => {
        if (a) {
          const projectRoles = a.getProjectrolesList().map(pr => pr.toObject());
          const currentProjectRole = get().currentProjectRole;

          let newCurrentProjectRole = currentProjectRole;
          if (
            Object.keys(currentProjectRole).length === 0 &&
            projectRoles.length > 0
          ) {
            newCurrentProjectRole = projectRoles[0];
          } else if (projectRoles.length > 0) {
            const matchedRole = projectRoles.find(
              role => role.id === currentProjectRole.id,
            );
            newCurrentProjectRole = matchedRole || projectRoles[0];
          }

          const orgRole = a.getOrganizationrole()?.toObject();

          set({
            currentUser: a.getUser()?.toObject(),
            token: a.getToken()?.toObject(),
            organizationRole: orgRole,
            // Set current org from the gRPC response (will be enriched by fetchOrganizationRoles)
            currentOrganizationRole: orgRole || ({} as OrganizationRole.AsObject),
            projectRoles: a.getProjectrolesList().map(pr => pr.toObject()),
            currentProjectRole: newCurrentProjectRole,
            featurePermissions: a
              .getFeaturepermissionsList()
              .map(p => p.toObject()),
          });

          // Fetch all org roles (REST) to populate the org switcher
          setTimeout(() => get().fetchOrganizationRoles(), 100);
          // Fetch platform_role from /v1/user/me (proto doesn't carry it)
          setTimeout(() => get().fetchCurrentUser(), 100);
        } else {
          set({});
        }
        callback();
      },
      isAuthenticated: () => !!get().token.token,
      authorize: (
        onSuccess: () => void,
        onFailure: (error: string) => void,
      ) => {
        const { token, currentUser } = get();
        if (!token.token) {
          onFailure('Missing token');
          return;
        }
        AuthorizeUser(
          connectionConfig,
          (err: ServiceError | null, auth: AuthenticateResponse | null) => {
            if (err) {
              onFailure(err.message);
              return;
            }
            if (auth?.getSuccess()) {
              get().setAuthentication(auth.getData(), onSuccess);
              onSuccess();
            } else {
              onFailure('Authorization failed');
            }
          },
          {
            authorization: token.token,
            'x-auth-id': currentUser.id,
          },
        );
      },
      projectRoles: [],
      organizationRole: {} as OrganizationRole.AsObject,
      organizationRoles: [] as OrganizationRole.AsObject[],
      currentOrganizationRole: {} as OrganizationRole.AsObject,
      currentProjectRole: {} as ProjectRole.AsObject,
      setCurrentProjectRole: (p: ProjectRole.AsObject) =>
        set({ currentProjectRole: p }),

      // Fetch all orgs accessible to the user (REST call)
      fetchOrganizationRoles: async () => {
        const { token, currentUser, currentOrganizationRole } = get();
        if (!token.token || !currentUser.id) return;
        try {
          const res = await fetch(`${REST_BASE}/v1/admin/user-organizations`, {
            headers: {
              'authorization': token.token,
              'x-auth-id': String(currentUser.id),
            },
          });
          if (!res.ok) return;
          const data = await res.json();
          const roles = (data.data || []) as OrganizationRole.AsObject[];
          set({ organizationRoles: roles });

          // If no current org selected, or current doesn't match any, pick first
          if (roles.length > 0) {
            const currentId = currentOrganizationRole?.organizationid;
            const matched = roles.find(r => r.organizationid === currentId);
            if (!matched) {
              set({ currentOrganizationRole: roles[0] });
            }
          }
        } catch (err) {
          console.error('[auth-store] fetchOrganizationRoles failed:', err);
        }
      },

      // Switch org: update current org + reload projects for the new org
      setCurrentOrganizationRole: async (o: OrganizationRole.AsObject) => {
        set({
          currentOrganizationRole: o,
          organizationRole: o,
        });

        // Fetch project roles for the new org
        const { token, currentUser } = get();
        if (!token.token || !currentUser.id || !o.organizationid) return;
        try {
          const res = await fetch(
            `${REST_BASE}/v1/admin/organizations/${o.organizationid}/projects`,
            {
              headers: {
                'authorization': token.token,
                'x-auth-id': String(currentUser.id),
              },
            },
          );
          if (!res.ok) return;
          const data = await res.json();
          const projectRoles = (data.data || []) as ProjectRole.AsObject[];
          set({
            projectRoles,
            currentProjectRole: projectRoles.length > 0 ? projectRoles[0] : {} as ProjectRole.AsObject,
          });
        } catch (err) {
          console.error('[auth-store] Failed to fetch projects for org:', err);
        }
      },

      isThereOrganization: () => {
        const state = get();
        return (
          state.organizationRole &&
          Object.keys(state.organizationRole).length > 0
        );
      },
      isThereProject: () => {
        return get().projectRoles && Object.keys(get().projectRoles).length > 0;
      },
      featurePermissions: [],
      isFeatureEnable: (feature: string) => {
        for (const permission of get().featurePermissions) {
          const pattern = new RegExp(permission.feature);
          if (pattern.test(feature)) {
            return permission.isenable;
          }
        }
        return false;
      },

      platformRole: '',
      isSuperAdmin: () => get().platformRole === 'superadmin',
      fetchCurrentUser: async () => {
        const { token, currentUser } = get();
        if (!token.token || !currentUser.id) return;
        try {
          const res = await fetch(`${REST_BASE}/v1/user/me`, {
            headers: {
              'authorization': token.token,
              'x-auth-id': String(currentUser.id),
            },
          });
          if (!res.ok) return;
          const data = await res.json();
          set({ platformRole: data.platform_role || 'user' });
        } catch (err) {
          console.error('[auth-store] fetchCurrentUser failed:', err);
        }
      },

      unauthenticate: () => {
        localStorage.clear();
        set({
          currentUser: {} as User.AsObject,
          token: {} as Token.AsObject,
          organizationRole: {} as OrganizationRole.AsObject,
          organizationRoles: [],
          currentOrganizationRole: {} as OrganizationRole.AsObject,
          projectRoles: [],
          currentProjectRole: {} as ProjectRole.AsObject,
          featurePermissions: [],
          platformRole: '',
        });
      },
    }),
    {
      version: 2,
      onRehydrateStorage: () => state => {},
      name: 'rpd::__user',
    },
  ),
);
